import React, { useRef, useState, useEffect } from "react";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import { useTranslation } from "react-i18next";

export default function MapLocationSearchInput({
    address = "",
    onSelectPlace,
    placeholder,
    className = "",
}) {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const resolvedPlaceholder = placeholder || t("pets.search_location_placeholder", { defaultValue: "Buscar dirección o lugar..." });

    const {
        ready,
        value,
        suggestions: { status, data },
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        debounce: 300,
        defaultValue: address || "",
    });

    // Ensure viewport does not auto-zoom on mobile WebKit/iOS on focus
    useEffect(() => {
        const meta = document.querySelector('meta[name="viewport"]');
        if (meta) {
            meta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
        }
    }, []);

    // Synchronize with external address changes (e.g., when map center changes)
    useEffect(() => {
        if (!isFocused && address !== undefined) {
            setValue(address, false);
        }
    }, [address, isFocused, setValue]);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, []);

    const handleInputChange = (e) => {
        setValue(e.target.value);
        setIsOpen(true);
    };

    const handleClear = () => {
        setValue("", false);
        clearSuggestions();
        setIsOpen(false);
    };

    const handleSelectSuggestion = async (suggestion) => {
        const { place_id, description } = suggestion;
        setValue(description, false);
        clearSuggestions();
        setIsOpen(false);

        try {
            const results = await getGeocode({ placeId: place_id });
            if (results && results[0]) {
                const { lat, lng } = getLatLng(results[0]);
                const components = results[0].address_components || [];
                const getComp = (types) => components.find((c) => types.some((t) => c.types.includes(t)));
                const cityComp =
                    getComp(["locality", "postal_town"]) ||
                    getComp(["sublocality", "sublocality_level_1"]) ||
                    getComp(["administrative_area_level_2"]);
                const countryComp = getComp(["country"]);
                const cityName = cityComp?.long_name;
                const municipality = cityName || description;

                onSelectPlace?.({
                    address: description,
                    lat,
                    lng,
                    municipality,
                    country: countryComp?.long_name || "",
                    countryCode: countryComp?.short_name || "",
                    placeId: place_id,
                    slug: municipality,
                });
            }
        } catch (error) {
            console.error("Geocoding failed for place:", error);
        }
    };

    return (
        <div ref={containerRef} className={`relative w-full ${className}`}>
            <div className="relative flex items-center">
                {/* Location Icon on the left */}
                <span className="absolute left-2.5 flex items-center pointer-events-none text-subtle-light dark:text-subtle-dark">
                    <svg className="w-3.5 h-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                    </svg>
                </span>

                {/* Search Input - Clean, text-xs, normal font weight, soft text color */}
                <input
                    type="text"
                    value={value}
                    onChange={handleInputChange}
                    onFocus={() => {
                        setIsFocused(true);
                        if (data.length > 0) setIsOpen(true);
                    }}
                    onBlur={() => setIsFocused(false)}
                    disabled={!ready}
                    placeholder={resolvedPlaceholder}
                    className="w-full pl-8 pr-7 py-2.5 text-xs bg-white dark:bg-card-dark rounded border border-border-light dark:border-border-dark text-neutral-700 dark:text-neutral-200 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 font-normal outline-none focus:outline-none focus:ring-0 focus:border-primary transition-colors shadow-sm"
                />

                {/* Clear button */}
                {value && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute right-2 w-4 h-4 rounded-full flex items-center justify-center text-subtle-light dark:text-subtle-dark hover:text-foreground-light dark:hover:text-foreground-dark transition-colors"
                        aria-label="Clear location search"
                    >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Suggestions Dropdown - Clean flat border without excessive shadow, text-xs, normal font */}
            {isOpen && status === "OK" && data.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-card-dark rounded border border-border-light dark:border-border-dark max-h-56 overflow-y-auto z-50 overscroll-contain shadow-sm">
                    {data.map((suggestion) => (
                        <button
                            type="button"
                            key={suggestion.place_id}
                            onClick={() => handleSelectSuggestion(suggestion)}
                            className="w-full text-left px-3 py-1.5 flex items-start gap-2 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors border-b last:border-b-0 border-border-light/40 dark:border-border-dark/40"
                        >
                            <svg
                                className="w-3.5 h-3.5 text-subtle-light dark:text-subtle-dark mt-0.5 shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                            </svg>
                            <div className="min-w-0 flex-1">
                                <div className="text-xs font-normal text-neutral-700 dark:text-neutral-200 truncate">
                                    {suggestion.structured_formatting?.main_text || suggestion.description}
                                </div>
                                {suggestion.structured_formatting?.secondary_text && (
                                    <div className="text-xs font-normal text-neutral-400 dark:text-neutral-500 truncate">
                                        {suggestion.structured_formatting.secondary_text}
                                    </div>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
